import React, {
  useState,
  useRef,
  forwardRef,
  Fragment,
  useCallback,
} from "react";
import {
  Row,
  Col,
  NMForm,
  useSub,
  usePub
} from "@neumetric/blueprint-lib/components";
import { useSelector, } from "react-redux";
import { Collapse } from "antd";
import { useParams } from "react-router-dom";
import { RiskResponder, RemoveResponderPopup } from "./Responders";
import { RiskConfirmationPopup } from "./Dialogs";
import {
  getDepartments,
  getActiveUsers,
  getCurrentUser,
  isOrganizer,
  isGoverner,
} from "@neumetric/blueprint-lib/store";
import {
  getDecisionList,
  getSeverityList,
  getRiskStatusList,
  getCurrentRisk,
  getAssetsList,
  getAssetTypesList,
  getThreatsList,
  getCategoryList,
  getSourcesList,
  getVulnerabilitiesList,
  isRiskAccepted,
  isRiskArchived,
  getSeverityByScore,
} from "../../store";

const FormFields = [
  {
    label: "Context",
    attribute: "context",
    typeRepresentation: "TEXT",
    placeholder: "Context",
    labelAlign: "middle",
    classes: { root: "w-100 mb-16", label: "w-100-px" },
  },
  {
    label: "Consequence",
    attribute: "consequence",
    typeRepresentation: "TEXT",
    placeholder: "Consequence",
    labelAlign: "middle",
    classes: { root: "w-100", label: "w-100-px" },
  },
  {
    label: "Stackholder",
    attribute: "stakeholder_id",
    placeholder: "StackHolder",
    labelAlign: "middle",
    typeRepresentation: "DROPDOWN",
    template: "USER",
    searchableFlag: true,
    userExpandMode: "POPUP",
    classes: { root: "w-40 mb-0", label: "w-100-px" },
  },
  {
    label: "Asset type",
    attribute: "asset_type_id",
    typeRepresentation: "DROPDOWN",
    placeholder: "Asset type",
    labelAlign: "middle",
    classes: { root: "w-55 mb-7", label: "w-93-px" },
  },
  {
    label: "Department",
    attribute: "department",
    typeRepresentation: "DROPDOWN",
    placeholder: "Department",
    labelAlign: "middle",
    classes: { root: "w-40 mb-0", label: "w-100-px" },
  },
  {
    label: "Asset",
    attribute: "asset_id",
    typeRepresentation: "DROPDOWN",
    placeholder: "Asset",
    labelAlign: "middle",
    classes: { root: "w-55 mb-7", label: "w-93-px" },
  },
  {
    attribute: "responder",
    typeRepresentation: "CUSTOM",
    component: <RiskResponder />,
  },
  {
    className: "flex-wrap w-55",
    childrenFields: [
      {
        label: "Threat",
        attribute: "threat_id",
        typeRepresentation: "DROPDOWN",
        placeholder: "Threat",
        labelAlign: "middle",
        classes: { root: "w-100 mb-7", label: "w-93-px" },
      },
      {
        label: "Vulnerability",
        attribute: "vulnerability_id",
        typeRepresentation: "DROPDOWN",
        placeholder: "Vulnerability",
        labelAlign: "middle",
        classes: { root: "w-100 mb-7", label: "w-93-px" },
      },
      {
        label: "Source",
        attribute: "source_id",
        typeRepresentation: "DROPDOWN",
        placeholder: "Source",
        labelAlign: "middle",
        classes: { root: "w-100 mb-7", label: "w-93-px" },
      },
      {
        label: "Category",
        attribute: "category",
        typeRepresentation: "DROPDOWN",
        placeholder: "Category",
        labelAlign: "middle",
        classes: { root: "w-100 mb-7", label: "w-93-px" },
      },
    ],
  },
];

const RatingFormFields = [
  {
    label: "Inherent Rating",
    attribute: "rating",
    typeRepresentation: "SCORE_GROUP",
    layout: "vertical",
    groups: [
      {
        label: "Confidentiality:",
        attribute: "confidentiality",
        classes: { label: "w-170-px" },
      },
      {
        label: "Integrity:",
        attribute: "integrity",
        classes: { label: "w-170-px" },
      },
      {
        label: "Availability:",
        attribute: "availability",
        classes: { label: "w-170-px" },
      },
      {
        label: "Security:",
        attribute: "security",
        classes: { label: "w-170-px" },
      },
      {
        label: "Privacy:",
        attribute: "privacy",
        classes: { label: "w-170-px" },
      },
    ],
    classes: { root: "w-100 mb-10", label: "fs-20 fw-600" },
  },
  {
    label: "Impact",
    attribute: "impact",
    typeRepresentation: "SCORE",
    type: "PROGRESS",
    readOnly: true,
    classes: { root: "w-100 mb-4", label: "fs-20 fw-600 w-156-px" },
  },
  {
    label: "Probability",
    attribute: "probability",
    typeRepresentation: "SCORE",
    classes: { root: "w-100", label: "fs-20 fw-600 w-156-px" },
  },
  {
    label: "Score",
    attribute: "score",
    typeRepresentation: "SCORE",
    type: "PROGRESS",
    readOnly: true,
    classes: { root: "w-100 mb-0", label: "fs-22 fw-600 w-156-px" },
    tooltip: "The score of the risk",
  },
];

const ResidualRatingFormFields = [
  {
    label: "Impact",
    attribute: "residual_impact",
    typeRepresentation: "SCORE",
    type: "PROGRESS",
    onChangeMode: "CONFIRM",
    classes: { root: "w-100 mb-4", label: "fs-20 fw-600 w-156-px" },
  },
  {
    label: "Probability",
    attribute: "residual_probability",
    typeRepresentation: "SCORE",
    onChangeMode: "CONFIRM",
    classes: { root: "w-100", label: "fs-20 fw-600 w-156-px" },
  },
  {
    label: "Score",
    attribute: "residual_score",
    typeRepresentation: "SCORE",
    type: "PROGRESS",
    readOnly: true,
    classes: { root: "w-100", label: "fs-22 fw-600 w-156-px" },
    tooltip: "The score of the risk",
  },
];


export const InfoSection = forwardRef((props, ref) => {
  const publish = usePub();
  const currentUser = useSelector(getCurrentUser);
  const { handleAutoUpdate } = props;
  const { riskId } = useParams();
  const [state, setState] = useState({
    dialogConfig: null,
    isEditMode: Boolean(!riskId),
    isSubmitting: false,
    showStackholderPopup: null,
  });
  const currentRisk = useSelector(getCurrentRisk);
  const _ref = useRef(null);
  const _ratingRef = useRef(null);
  const _residualRatingRef = useRef(null);
  const departments = useSelector(getDepartments);
  const decisionList = useSelector(getDecisionList);
  const severityList = useSelector(getSeverityList);
  const statusList = useSelector(getRiskStatusList);
  const assetTypesList = useSelector(getAssetTypesList);
  const assetsList = useSelector(getAssetsList);
  const threatsList = useSelector(getThreatsList);
  const vulnerabilitiesList = useSelector(getVulnerabilitiesList);
  const sourcesList = useSelector(getSourcesList);
  const categoryList = useSelector(getCategoryList);
  const activeUsers = useSelector(getActiveUsers);
  const isRiskAccept = useSelector(isRiskAccepted);
  const isRiskArchive = useSelector(isRiskArchived);
  const isOrganizerUser = isOrganizer(currentUser);
  const isGovernerUser = isGoverner(currentUser);

  useSub("RISK_EDIT_MODE", (value) => {
    if (value) {
      updateState({ isEditMode: true });
    } else {
      handleCancel();
    }
  })

  const updateState = (newState) => {
    setState({ ...state, ...newState });
  };

  const handleUserExpand = (user) => {
    updateState({ showStackholderPopup: user ? { ent_usr: user } : user });
  };

  const getInfoFields = useCallback(() => {
    return FormFields.map((field) => {
      const newFields = { ...field };
      if (field.attribute === "stakeholder_id") {
        newFields.readOnly = !(isOrganizerUser || isGovernerUser);
        newFields.onUserExpand = handleUserExpand;
      }
      if (newFields.typeRepresentation === "DROPDOWN") {
        newFields.placeholder = state.isEditMode ? newFields.placeholder : "Undefined";
      }
      if (newFields.attribute === "context") {
        newFields.placeholder = state.isEditMode ? newFields.placeholder : "No Context available.";
      }
      if (newFields.attribute === "consequence") {
        newFields.placeholder = state.isEditMode ? newFields.placeholder : "No Consequence available.";
      }
      if (newFields.childrenFields) {
        newFields.childrenFields = newFields.childrenFields.map((child) => {
          const newChild = { ...child };
          if (child.typeRepresentation === "DROPDOWN") {
            newChild.placeholder = state.isEditMode ? newChild.placeholder : "Undefined";
          }
          return newChild;
        });
      }
      return newFields;
    });
  }, [isOrganizerUser, isGovernerUser, state.isEditMode]);

  const getOptionsWithAdditionalOption = useCallback((options, idAttribute, textAttribute) => {
    const mapOptions = options.map((option) => ({
      ...option,
    }));
    const textValue = currentRisk[textAttribute] || '';
    const idValue = currentRisk[idAttribute] || "";
    const valueOption = mapOptions.find((option) => String(option.id).toLowerCase() === textValue.toLowerCase());
    if (!idValue && !valueOption && textValue) {
      mapOptions.push({ id: textValue, label: textValue });
    }
    return mapOptions;
  }, [currentRisk]);

  const getOptions = (attribute) => {
    if (attribute === "department") {
      return getOptionsWithAdditionalOption(departments, attribute, attribute);
    }
    if (attribute === "decision") {
      return decisionList;
    }
    if (attribute === "severity") {
      return severityList;
    }
    if (attribute === "risk_status") {
      return statusList;
    }
    if (attribute === "asset_type_id") {
      const options = (assetTypesList || []).map((assetType) => ({
        id: assetType.id,
        label: assetType.asset_type,
      }));
      return getOptionsWithAdditionalOption(options, attribute, "asset_type_val");
    }
    if (attribute === "asset_id") {
      const options = (assetsList || []).map((asset) => ({
        id: asset.id,
        label: asset.asset,
      }));
      return getOptionsWithAdditionalOption(options, attribute, "asset_val");
    }
    if (attribute === "stakeholder_id") {
      return activeUsers;
    }
    if (attribute === "source_id") {
      const options = (sourcesList || []).map((source) => ({
        id: source.id,
        label: source.name,
      }));
      return getOptionsWithAdditionalOption(options, attribute, "source_val");
    }
    if (attribute === "vulnerability_id") {
      const options = (vulnerabilitiesList || []).map((vulnerability) => ({
        id: vulnerability.id,
        label: vulnerability.vulnerability,
      }));
      return getOptionsWithAdditionalOption(options, attribute, "vulnerability_val");
    }
    if (attribute === "threat_id") {
      const options = (threatsList || []).map((threat) => ({
        id: threat.id,
        label: threat.threat,
      }));
      return getOptionsWithAdditionalOption(options, attribute, "threat_val");
    }
    if (attribute === "category") {
      const options = (categoryList || []).map((category) => ({
        id: category.id,
        label: category.label,
      }));
      return getOptionsWithAdditionalOption(options, attribute, attribute);
    }
    return [];
  };

  const handleInfoChange = (attribute, value) => {
    publish("NM_OBJECT_HEADER:CHANGE")
    if (attribute === "vulnerability_id") {
      const selected = (vulnerabilitiesList || []).find(
        (v) => String(v.id) === String(value)
      );

      const vulnCategory = selected?.category;
      if (vulnCategory) {
        _ref.current.updateFormData({ category: vulnCategory });
      }
    }
  };

  const formInitialValues = React.useMemo(() => {
    let initialValues = { severity: "Minimal" };
    const mappingFields = [
      { id: "asset_type_id", text: "asset_type_val" },
      { id: "asset_id", text: "asset_val" },
      { id: "source_id", text: "source_val" },
      { id: "vulnerability_id", text: "vulnerability_val" },
      { id: "threat_id", text: "threat_val" },
      { id: "category", text: "category" },
    ]
    if (riskId && currentRisk) {
      initialValues = {
        ...currentRisk,
      };
      mappingFields.forEach((field) => {
        if (!initialValues[field.id]) {
          initialValues[field.id] = currentRisk[field.text];
        }
      });
    }
    return initialValues;
  }, [riskId, currentRisk]);

  const validate = () => {
    return (
      _ref.current.validate() &&
      _ratingRef.current.validate() &&
      _residualRatingRef.current.validate()
    );
  };

  const getValues = () => {
    const options = { dirtyOnly: true };
    let ratingValues = _ratingRef.current.getValues(options);
    ratingValues = { ...(ratingValues.rating || {}), ...ratingValues };
    let residualRatingValues = _residualRatingRef.current.getValues(options);
    residualRatingValues = {
      ...(residualRatingValues.residual_rating || {}),
      ...residualRatingValues,
    };
    delete ratingValues.rating;
    delete residualRatingValues.residual_rating;
    return {
      ..._ref.current.getValues(options),
      ...ratingValues,
      ...residualRatingValues,
    };
  };

  React.useImperativeHandle(ref, () => ({
    validate: validate,
    getValues: getValues,
    getDirtyFields: getModifiedFields,
    refresh: handleCancel,
    getModifiedFieldsComment: getModifiedFieldsComment
  }));

  const getModifiedFields = () => {
    const ratingModifiedFields = _ratingRef.current.getDirtyFields();
    const infoModifiedFields = _ref.current.getDirtyFields();
    const residualRatingModifiedFields =
      _residualRatingRef.current.getDirtyFields();
    const fields = [
      ...infoModifiedFields,
      ...ratingModifiedFields,
      ...residualRatingModifiedFields,
    ];
    return fields;
  };

  const getValueComment = (value) => {
    return `<span class="fw-600">${value}</span>`;
  };
  const getLabelComment = (label, margin, width) => {
    return `<div class="d-inline-block ${width || "w-115-px"
      } mr-2 ${margin}">${label}</div>`;
  };

  const getModifiedFieldsComment = React.useCallback(
    (fields, newValues) => {
      const evalFields = [
        "risk_status",
        "decision",
        "impact",
        "score",
        "residual_score",
      ];
      let comment = "";
      let evalComment = [];
      let residualRatingComment = "";
      fields.forEach((field, index) => {
        const isResidualRating = (field.attribute || "").startsWith(
          "residual_"
        );
        const isScoreGroup = field.typeRepresentation === "SCORE_GROUP";
        const isEvalField = evalFields.includes(field.attribute);
        const oldValue = currentRisk[field.attribute];
        const newValue = newValues[field.attribute];

        if (field.typeRepresentation === "TEXT") {
          comment += `Updated ${field.label}.`;
        } else if (field.typeRepresentation === "DROPDOWN") {
          const options = getOptions(field.attribute);
          const oldOption = options.find((option) => option.id === oldValue);
          const newOption = options.find((option) => option.id === newValue);
          if (oldOption && newOption && oldOption.id !== newOption.id) {
            const localcomment = `${getLabelComment(
              `Updated ${field.label}:`
            )}From ${getValueComment(
              oldOption.label || oldOption.name
            )} to ${getValueComment(newOption.label || newOption.name)}.`;

            if (evalFields.includes(field.attribute)) {
              evalComment.push(localcomment);
            } else {
              comment += localcomment;
            }
          }
        } else if (field.typeRepresentation === "SCORE") {
          let scoreComment = `From ${getValueComment(
            oldValue
          )} to ${getValueComment(newValue)}.`;
          if (isResidualRating) {
            residualRatingComment += `${getLabelComment(
              `${field.label}:`,
              "ml-8",
              "w-106-px"
            )}${scoreComment} <br/>`;
          } else {
            const scorecomment = `${getLabelComment(
              `Updated ${field.label}:`
            )}${scoreComment}`;
            if (evalFields.includes(field.attribute)) {
              evalComment.push(scorecomment);
            } else {
              comment += scorecomment;
            }
          }
        } else if (field.typeRepresentation === "SCORE_GROUP") {
          comment += `Updated ${field.label}: <br/>`;
          const groups = RatingFormFields.find(
            (f) => f.attribute === field.attribute
          ).groups;
          groups.forEach((group, iterator) => {
            const oldValue = currentRisk[group.attribute];
            const newValue = newValues[group.attribute];
            if (newValue !== undefined && newValue !== oldValue) {
              comment += `${getLabelComment(
                `${group.label}`,
                "ml-8",
                "w-106-px"
              )}From ${getValueComment(oldValue)} to ${getValueComment(
                newValue
              )}.<br/>`;
            }
          });
        }

        if (residualRatingComment && index === fields.length - 1) {
          comment += `Updated Residual Rating: <br/>${residualRatingComment}`;
        }
        if (
          !isResidualRating &&
          !isScoreGroup &&
          !isEvalField &&
          comment.length > 0 &&
          index < fields.length - 1
        ) {
          comment += "<br/>";
        }
      });
      const comments = [{ comment: comment }];
      if (evalComment.length > 0) {
        comments.push({
          comment: evalComment.join("<br/>"),
          is_eval_comment: true,
        });
      }
      return comments;
    },
    [currentRisk]
  );

  const handleCancel = () => {
    updateState({ isEditMode: false, isSubmitting: false, dialogConfig: null });
    const timerId = setTimeout(() => {
      clearTimeout(timerId);
      _ratingRef.current.resetFormData();
      _ref.current.resetFormData();
      _residualRatingRef.current.resetFormData();
    }, 500);
  };

  const handleProbabilitySync = (value) => {
    _residualRatingRef.current.updateFormData({ residual_probability: value });
    handleResidualChange("residual_probability", value);
  };

  const handleChange = (attribute, value) => {
    const values = _ratingRef.current.getValues();
    const newValues = {};
    if (attribute === "rating") {
      let impact = Object.values(value || {}).reduce((a, b) => {
        if (b > -1) {
          return a + b;
        }
        return a;
      }, 0);
      if (impact > -1) {
        impact = Math.round(impact / 5);
        let score = values.score;
        if (values.probability > -1) {
          score = Math.round((impact * values.probability) / 5);
        }
        newValues.impact = impact;
        newValues.score = score;
      }
    }
    if (attribute === "probability") {
      const score = Math.round((values.impact * value) / 5);
      if (score > -1) {
        newValues.score = score;
      }
      handleProbabilitySync(value);
    }
    if (Object.keys(newValues).length > 0) {
      if (newValues.score !== undefined) {
        handleDecisionAutoUpdate(null, newValues.score);
      }
      _ratingRef.current.updateFormData(newValues);
      publish("NM_OBJECT_HEADER:CHANGE")
    }
  };

  const handleDecisionAutoUpdate = (residualScore, initialScore) => {
    if (!residualScore) {
      const values = _residualRatingRef.current.getValues();
      residualScore = values.residual_score;
    }
    const score = residualScore !== undefined ? residualScore : initialScore;
    if (score > -1) {
      const decision = score <= 3 ? "acceptable" : "treat";
      const status = score <= 3 ? "accepted" : "pending";
      const severity = getSeverityByScore(severityList, score);
      handleAutoUpdate({ decision, status, severity });
    }
  };

  const handleResidualChangeConfirm = (dialogConfig) => {
    const { attribute, newValue } = dialogConfig;
    updateState({ dialogConfig: null });
    const values = _residualRatingRef.current.getValues();
    const newValues = { [attribute]: newValue };
    if (attribute === "residual_impact") {
      const score = Math.round((newValue * values.residual_probability) / 5);
      newValues.residual_score = score;
    }
    if (attribute === "residual_probability") {
      const score = Math.round((values.residual_impact * newValue) / 5);
      newValues.residual_score = score;
    }
    if (Object.keys(newValues).length > 0) {
      _residualRatingRef.current.updateFormData(newValues);
      handleDecisionAutoUpdate(newValues.residual_score);
      publish("NM_OBJECT_HEADER:CHANGE")
    }
  };

  const handleResidualChange = (attribute, value) => {
    const label = attribute === "residual_impact" ? "Impact" : "Probability";
    const description = (
      <span>
        Are you sure you want to update the Residual {label} Rating?
        <br />
        <br />
        This will update the Risk Residual Score, Decision, Severity & Status.
      </span>
    );
    const dialogConfig = {
      title: `Change Residual ${label}?`,
      attribute: attribute,
      newValue: value,
      description: description,
      confirm: { onClick: handleResidualChangeConfirm, icon: "CHECK_CONFIRM" },
      cancel: { onClick: handleDialogClose, icon: "CANCEL", btnType: "TEXT" },
    };
    updateState({ dialogConfig: dialogConfig });
  };

  const handleDialogClose = () => {
    updateState({ dialogConfig: null });
  };

  const header = (
    <Row align="center" justify="between" className="w-100">
      <span className="ff-exo2 fs-18 fw-600 line-28 text-filled">
        Information
      </span>
    </Row>
  );

  return (
    <Fragment>
      <Collapse
        className="w-100 mb-16 mt-16"
        defaultActiveKey={["info"]}
        collapsible="disabled"
      >
        <Collapse.Panel key="info" header={header} showArrow={false}>
          <Row className="w-100" align="start">
            <Col className="w-50 mr-16">
              <NMForm
                ref={_ref}
                className={state.isEditMode ? "" : "risk-info-form"}
                fields={getInfoFields()}
                formData={formInitialValues}
                getOptions={getOptions}
                onChange={handleInfoChange}
                readOnly={!state.isEditMode || isRiskAccept || isRiskArchive}
              />
            </Col>
            <Row className="w-50 ml-16">
              <Col className="w-50">
                <NMForm
                  ref={_ratingRef}
                  onChange={handleChange}
                  fields={RatingFormFields}
                  formData={formInitialValues}
                  readOnly={!state.isEditMode || isRiskAccept || isRiskArchive}
                />
              </Col>
              <Col className="w-50">
                <span className="ff-exo2 fs-18 fw-600 line-28 text-filled mb-40">
                  Residual Rating:
                </span>
                <NMForm
                  ref={_residualRatingRef}
                  onChange={handleResidualChange}
                  fields={ResidualRatingFormFields}
                  formData={formInitialValues}
                  readOnly={!state.isEditMode || isRiskAccept || isRiskArchive}
                />
              </Col>
            </Row>
          </Row>
        </Collapse.Panel>
      </Collapse>
      {state.dialogConfig && (
        <RiskConfirmationPopup
          onClose={handleDialogClose}
          dialogConfig={state.dialogConfig}
        />
      )}
      {state.showStackholderPopup && (
        <RemoveResponderPopup
          allowRemove={false}
          onClose={() => handleUserExpand(null)}
          responder={state.showStackholderPopup}
        />
      )}
    </Fragment>
  );
});
